import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTeam } from '../../../contexts/TeamContext'
import useMarketplacePackagesResources from '../../../shared/resources/api/v1/marketplace/useMarketplacePackagesResources'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import PageBreadcrumbs from '../../../shared/components/PageBreadcrumbs'
import PageSectionGrid from '../../../shared/components/layouts/PageSectionGrid'
import PackageImportCreateDialog from './components/PackageImportCreateDialog'
import PackageCard from './components/PackageCard'

function MarketplaceIndexPage() {
  const { teamSlug } = useParams()
  const { team } = useTeam()
  const { all } = useMarketplacePackagesResources()

  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedPackage, setSelectedPackage] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const loadData = useCallback(() => {
    setLoading(true)
    setError(null)
    all()
      .then((response) => {
        setPackages(response.packages || [])
      })
      .catch((err) => {
        setError(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [all])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleImportClick = (pkg) => {
    setSelectedPackage(pkg)
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setSelectedPackage(null)
  }

  if (loading) return <LoadingState />
  if (error) return <ErrorState messageId="marketplace.error" />

  return (
    <Container maxWidth={false}>
      <Box sx={{ my: 4 }}>
        <PageBreadcrumbs entities={{ team }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1">
            <FormattedMessage id="marketplace.title" />
          </Typography>
        </Box>

        {packages.length === 0 ? (
          <Typography color="text.secondary">
            <FormattedMessage id="marketplace.empty" />
          </Typography>
        ) : (
          <PageSectionGrid columns={3}>
            {packages.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} onImport={handleImportClick} />
            ))}
          </PageSectionGrid>
        )}
      </Box>

      <PackageImportCreateDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        pkg={selectedPackage}
      />
    </Container>
  )
}

export default MarketplaceIndexPage
